// src/features/Planning/components/Step4PlotSize.jsx
import {
  Box,
  Flex,
  Text,
  Input,
  Image,
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  ModalFooter,
} from "@chakra-ui/react";
import { useState, useRef } from "react";
import CompassImage from "../../../../assets/Planning/Compass/illustration-compass 1.png";
import NeighbourHomeImage from "../../../../assets/Planning/NeighboursHouse/Rectangle 9607.png";
import RoadImage from "../../../../assets/Planning/RoadImage/Group 35547.png";
import { normalizeDirection } from "../../../../utils/api";

function getSideImage(side, peripheries) {
  const value = peripheries?.[side];
  if (!value) return null;
  if (value === "Road") return RoadImage;
  if (value === "Neighbour Plot") return NeighbourHomeImage;
  return null;
}

// Step2Direction.jsx / Step3Peripheries.jsx jaisa hi angle map — taaki
// compass yahan bhi Step 2 mein select ki hui direction ke hisaab se
// sahi rotate ho (pehle ye static/hardcoded tha, direction ignore karta tha).
const directionAngles = {
  north: 0,
  "north-east": 45,
  east: 90,
  "south-east": 135,
  south: 180,
  "south-west": 225,
  west: 270,
  "north-west": 315,
};

function FtInRow({ label, feetKey, inchesKey, required, size, onFeetChange, onInchesChange }) {
  const inputStyles = {
    h: "38px",
    borderRadius: "8px",
    fontSize: "14px",
    textAlign: "center",
    fontWeight: "500",
    borderColor: "gray.300",
    _hover: { borderColor: "cyan.400" },
    _focus: {
      borderColor: "cyan.500",
      boxShadow: "0 0 0 1px var(--chakra-colors-cyan-500)",
    },
  };

  return (
    <Box mb={3.5}>
      <Text fontSize="13px" mb={1.5} color="gray.600" fontWeight="600">
        {label}{" "}
        {required && (
          <Text as="span" color="red.500">
            *
          </Text>
        )}
      </Text>

      <Flex gap={3}>
        <Box flex="1">
          <Text fontSize="10px" color="gray.400" mb={1} textAlign="center">
            Feet
          </Text>
          <Input
            {...inputStyles}
            value={size[feetKey] ?? ""}
            onChange={(e) => onFeetChange(feetKey, e.target.value)}
            placeholder="30"
          />
        </Box>

        <Box flex="1">
          <Text fontSize="10px" color="gray.400" mb={1} textAlign="center">
            Inches
          </Text>
          <Input
            {...inputStyles}
            value={size[inchesKey] ?? "0"}
            onChange={(e) => onInchesChange(inchesKey, e.target.value)}
            placeholder="0"
          />
        </Box>
      </Flex>
    </Box>
  );
}

export default function Step4PlotSize({
  formData,
  setFormData,
  onNext,
  onSubmit,
  onBack,
  isLastStep,
  viewMode,
}) {
  const peripheries = formData?.peripheries ?? {};
  const size = formData?.size ?? {};

  const originalSize = useRef(size);
  const [showChangeAlert, setShowChangeAlert] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const hasPrefill = !!(
    parseFloat(originalSize.current?.widthFeet) > 0 ||
    parseFloat(originalSize.current?.depthFeet) > 0
  );

  const handleFeetChange = (field, val) => {
    if (val !== "" && !/^\d*$/.test(val)) return;
    const newSize = { ...size, [field]: val };
    if (hasPrefill) setShowChangeAlert(true);
    setFormData({ ...formData, size: newSize });
  };

  const handleInchesChange = (field, val) => {
    if (val !== "" && (!/^\d*$/.test(val) || parseInt(val) > 11)) return;
    const newSize = { ...size, [field]: val };
    if (hasPrefill) setShowChangeAlert(true);
    setFormData({ ...formData, size: newSize });
  };

  const rightImage = getSideImage("right", peripheries);
  const backImage = getSideImage("back", peripheries);
  const leftImage = getSideImage("left", peripheries);

  // Step 2 mein select ki hui direction ke hisaab se compass rotate karo
  const normalizedDirection = normalizeDirection(formData?.direction);
  const selectedAngle = directionAngles[normalizedDirection] ?? 0;
  const compassRotation = normalizedDirection ? 180 - selectedAngle : 0;

  const isValid = () =>
    parseFloat(size.widthFeet) > 0 && parseFloat(size.depthFeet) > 0;

  const handleAction = () => {
    if (!viewMode && !isValid()) return;
    if (showChangeAlert) {
      setShowConfirm(true);
      return;
    }
    isLastStep ? onSubmit() : onNext();
  };

  const handleConfirmProceed = () => {
    setShowConfirm(false);
    setShowChangeAlert(false);
    isLastStep ? onSubmit() : onNext();
  };

  const handleCancelConfirm = () => {
    setShowConfirm(false);
  };

  return (
    <Box
      h="600px"
      w="100%"
      maxW="400px"
      mx="auto"
      display="flex"
      flexDirection="column"
      justifyContent="space-between"
      px={4}
      py={6}
    >
      <Box pointerEvents={viewMode ? "none" : "auto"}>
        {/* Heading */}
        <Text
          fontSize="17px"
          fontWeight="600"
          color="gray.800"
          mb={5}
          textAlign="center"
        >
          Select your plot Dimensions
        </Text>

        <Flex align="center" justify="space-between" mb={3}>
          <Text fontSize="12px" color="gray.400">
            Enter feet & inches
          </Text>

          {showChangeAlert && (
            <Box
              px="6px"
              py="2px"
              borderRadius="5px"
              bg="orange.50"
              border="1px solid #f59e0b"
            >
              <Text fontSize="10px" color="orange.600" fontWeight="600">
                Editing pre-filled
              </Text>
            </Box>
          )}
        </Flex>

        {/* Inputs */}
        <Box
          mb={4}
          bg="white"
          p={3.5}
          borderRadius="10px"
          border="1px solid"
          borderColor="gray.200"
        >
          <FtInRow
            label="Plot Width"
            feetKey="widthFeet"
            inchesKey="widthInches"
            required
            size={size}
            onFeetChange={handleFeetChange}
            onInchesChange={handleInchesChange}
          />

          <FtInRow
            label="Plot Depth"
            feetKey="depthFeet"
            inchesKey="depthInches"
            required
            size={size}
            onFeetChange={handleFeetChange}
            onInchesChange={handleInchesChange}
          />
        </Box>

        {/* Diagram */}
        <Box
          position="relative"
          height="190px"
          mx="auto"
          maxWidth="260px"
          bg="gray.50"
          borderRadius="10px"
          p={3}
        >
          <Image
            position="absolute"
            top="50%"
            left="50%"
            transform={`translate(-50%, -50%) rotate(${compassRotation}deg)`}
            transition="transform 0.4s ease-in-out"
            src={CompassImage}
            width="140px"
          />

          <Image
            position="absolute"
            bottom="6px"
            left="50%"
            transform="translateX(-50%)"
            src={RoadImage}
            width="95px"
          />

          {backImage && (
            <Image
              position="absolute"
              top="6px"
              left="50%"
              transform="translateX(-50%)"
              src={backImage}
              width="95px"
            />
          )}

          {leftImage && (
            <Image
              position="absolute"
              left="4px"
              top="50%"
              transform="translateY(-50%) rotate(-90deg)"
              src={leftImage}
              width="95px"
            />
          )}

          {rightImage && (
            <Image
              position="absolute"
              right="4px"
              top="50%"
              transform="translateY(-50%) rotate(90deg)"
              src={rightImage}
              width="95px"
            />
          )}
        </Box>
      </Box>

      {/* Buttons */}
      <Flex justify="space-between" gap={3}>
        <Button
          onClick={onBack}
          variant="solid"
          bg="white"
          color="cyan.600"
          border="1px solid"
          borderColor="cyan.500"
          borderRadius="6px"
          minW="100px"
          h="40px"
          fontWeight="500"
          fontSize="14px"
          _hover={{ bg: "cyan.50" }}
        >
          ← Previous
        </Button>

        <Button
          onClick={handleAction}
          isDisabled={viewMode ? false : !isValid()}
          variant="solid"
          bg="cyan.500"
          color="white"
          borderRadius="6px"
          minW="100px"
          h="40px"
          fontWeight="500"
          fontSize="14px"
          ml="auto"
          _hover={{ bg: "cyan.600" }}
          _disabled={{
            bg: "gray.300",
            color: "gray.500",
            cursor: "not-allowed",
          }}
        >
          {isLastStep ? "Submit" : "Next →"}
        </Button>
      </Flex>

      {/* ── Confirmation Modal — pre-filled data change alert ── */}
      <Modal
        isOpen={showConfirm}
        onClose={handleCancelConfirm}
        isCentered
        size="sm"
      >
        <ModalOverlay bg="blackAlpha.500" />
        <ModalContent mx={4} borderRadius="14px">
          <ModalBody pt={6} pb={2} textAlign="center">
            <Text fontSize="28px" mb={3}>⚠️</Text>
            <Text fontWeight="700" fontSize="16px" color="gray.800" mb={2}>
              Plot Size Changed
            </Text>
            <Text fontSize="14px" color="gray.600" lineHeight="1.6">
              You've changed the pre-filled plot dimensions. This may affect your
              setbacks and building area in the next steps.
            </Text>
            <Text fontSize="13px" color="cyan.600" fontWeight="600" mt={2}>
              Are you sure you want to continue?
            </Text>
          </ModalBody>
          <ModalFooter justifyContent="center" gap={3} pb={5}>
            <Button
              variant="outline"
              colorScheme="cyan"
              borderRadius="8px"
              px={6}
              onClick={handleCancelConfirm}
            >
              Go Back
            </Button>
            <Button
              colorScheme="cyan"
              borderRadius="8px"
              px={6}
              onClick={handleConfirmProceed}
            >
              Yes, Continue →
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}